import { Image, StyleSheet, View } from 'react-native';

import { Pill, Text } from '@/components/ui';
import { palette, useTheme } from '@/theme';
import type { Teacher } from '@/content/team';

const colorMap: Record<Teacher['color'], string> = {
  wine: palette.wine,
  ember: palette.ember,
  gold: palette.gold,
  verde: palette.verdeSlate,
  sand: palette.sandLilac,
  lilac: palette.wineSoft,
};

export function TeamGrid({ teachers }: { teachers: Teacher[] }) {
  return (
    <View style={styles.grid}>
      {teachers.map((t, i) => (
        <TeacherTile key={t.name + i} teacher={t} />
      ))}
    </View>
  );
}

function TeacherTile({ teacher }: { teacher: Teacher }) {
  const theme = useTheme();
  const tileBg = colorMap[teacher.color];
  return (
    <View
      style={[
        styles.tile,
        {
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radii.md,
          borderColor: theme.colors.rule,
        },
      ]}
    >
      <View
        style={[
          styles.portrait,
          { backgroundColor: tileBg },
        ]}
      >
        {teacher.photo ? (
          <Image
            source={teacher.photo}
            style={styles.portraitImage}
            resizeMode="cover"
            accessibilityLabel={teacher.name}
          />
        ) : (
          <Text
            style={{
              fontFamily: theme.fontFamily.displayItalic,
              fontStyle: 'italic',
              fontSize: 64,
              lineHeight: 64,
              color: teacher.color === 'gold' || teacher.color === 'sand' ? palette.ink : palette.paper,
            }}
          >
            {teacher.initials}
          </Text>
        )}
      </View>
      <View style={styles.body}>
        <Text variant="h4" style={{ marginBottom: 6 }}>
          {teacher.name}
        </Text>
        <Text variant="meta" tone="fgMute">
          {teacher.role}
        </Text>
        <Text variant="body" tone="fgSoft" style={{ marginTop: 8 }}>
          {teacher.style}
        </Text>
        {teacher.since ? (
          <Text variant="meta" tone="fgMute" style={{ marginTop: 12 }}>
            Seit {teacher.since}
          </Text>
        ) : null}
        {teacher.trainee ? (
          <View style={{ marginTop: 12 }}>
            <Pill variant="ghost">In Ausbildung</Pill>
          </View>
        ) : null}
        {teacher.placeholder ? (
          <View style={{ marginTop: 12 }}>
            <Pill variant="ember">Bewirb dich</Pill>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  tile: {
    flex: 1,
    minWidth: 220,
    maxWidth: 320,
    borderWidth: 1,
    overflow: 'hidden',
  },
  portrait: {
    height: 240,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  portraitImage: {
    width: '100%',
    height: '100%',
  },
  body: {
    padding: 20,
  },
});
